<template>
  <div style="padding: 1vw">
    <div style="display: flex; justify-content: space-between; align-items: flex-end; padding: 0 1vw">
      <div class="pageTitle">
        <h3 class="fw-bold" style="color: #000; font-size: 1.8rem">
          인증정보 관리
        </h3>
        <span class="pageDesc">
          알림톡 및 카카오클라우드 서비스에 사용되는 토큰을 관리합니다.
        </span>
      </div>
    </div>

    <div class="container-wrapper">
      <a-tabs v-model:activeKey="activeTab" type="card">
        <!-- 채널별 비즈메세지 관리 탭 -->
        <a-tab-pane key="channel" tab="채널별 비즈메세지 관리">
          <ChannelTokenManagement />
        </a-tab-pane>

        <!-- 비즈메시지 토큰 탭 -->
        <a-tab-pane key="bizmsg" tab="비즈메시지 토큰 관리 (deprecated)">
          <div class="tab-header">
            <a-button
              type="primary"
              @click="refreshBizmsgToken"
              :loading="bizmsgRefreshLoading"
              style="background-color: #FEE500; border-color: #FEE500; color: #000000;"
            >
              토큰 갱신
            </a-button>
          </div>

          <a-table
            class="ant-table-striped"
            size="small"
            :columns="bizmsgColumns"
            :data-source="bizmsgTokenList"
            :pagination="false"
            :loading="bizmsgLoading"
          >
            <template #emptyText>
              <div class="noResult">등록된 토큰이 없습니다.</div>
            </template>
          </a-table>
        </a-tab-pane>

        <!-- 카카오클라우드 토큰 탭 -->
        <a-tab-pane key="kakaocloud" tab="카카오클라우드 토큰 관리 (기계설비 전용)">
          <div class="tab-header">
            <a-button
              type="primary"
              @click="refreshKakaocloudToken"
              :loading="kakaocloudRefreshLoading"
              style="background-color: #FEE500; border-color: #FEE500; color: #000000;"
            >
              토큰 갱신
            </a-button>
          </div>

          <a-table
            class="ant-table-striped"
            size="small"
            :columns="kakaocloudColumns"
            :data-source="kakaocloudTokenList"
            :pagination="false"
            :loading="kakaocloudLoading"
          >
            <template #headerCell="{ column }">
              <template v-if="column.key === 'issuedAt'">
                <span>
                  발급일시
                  <a-tooltip placement="top">
                    <template #title>
                      <span>
                        토큰은 발급일시로 부터 12시간 후 만료됩니다. 자세한 내용은 <a href="https://docs.kakaocloud.com/start/api-preparation#get-api-authentication-token" target="_blank" rel="noopener noreferrer">카카오클라우드 API 인증 토큰 문서</a>를 참조하세요.
                      </span>
                    </template>
                    <info-circle-outlined style="margin-left: 4px;" />
                  </a-tooltip>
                </span>
              </template>
            </template>
            <template #emptyText>
              <div class="noResult">등록된 토큰이 없습니다.</div>
            </template>
          </a-table>
        </a-tab-pane>
      </a-tabs>
    </div>
  </div>
</template>

<script>
import { AlimtalkApi, KakaocloudApi } from '../api';
import { message } from 'ant-design-vue';
import { defineComponent } from 'vue';
import { InfoCircleOutlined } from '@ant-design/icons-vue';
import { oneBtnDialog } from '@/utils/utils';
import ChannelTokenManagement from './components/ChannelTokenManagement.vue';

export default defineComponent({
  name: "TokenManage",
  components: {
    InfoCircleOutlined,
    ChannelTokenManagement
  },
  data() {
    return {
      activeTab: 'channel',

      // 비즈메시지 토큰 관련
      bizmsgLoading: false,
      bizmsgRefreshLoading: false,
      bizmsgTokenList: [],
      bizmsgColumns: [
        {
          title: '번호',
          dataIndex: 'index',
          key: 'index',
          width: 80,
          customRender: ({ index }) => index + 1
        },
        {
          title: '회사명',
          dataIndex: 'companyName',
          key: 'companyName',
          width: 150
        },
        {
          title: '토큰',
          dataIndex: 'token',
          key: 'token',
          ellipsis: true,
          customRender: this.renderMaskedToken
        },
        {
          title: '발급일시',
          dataIndex: 'issuedAt',
          key: 'issuedAt',
          width: 180,
          customRender: this.renderDateTime
        },
        {
          title: '만료일시',
          dataIndex: 'expiresAt',
          key: 'expiresAt',
          width: 180,
          customRender: this.renderDateTime
        },
      ],

      // 카카오클라우드 토큰 관련
      kakaocloudLoading: false,
      kakaocloudRefreshLoading: false,
      kakaocloudTokenList: [],
      kakaocloudColumns: [
        {
          title: '번호',
          dataIndex: 'index',
          key: 'index',
          width: 80,
          customRender: ({ index }) => index + 1
        },
        {
          title: '회사명',
          dataIndex: 'companyName',
          key: 'companyName',
          width: 150
        },
        {
          title: '토큰',
          dataIndex: 'token',
          key: 'token',
          ellipsis: true,
          customRender: this.renderMaskedToken
        },
        {
          title: '발급일시',
          dataIndex: 'issuedAt',
          key: 'issuedAt',
          width: 180,
          customRender: this.renderDateTime
        },
      ]
    };
  },
  methods: {
    // 공통 메서드: 토큰 마스킹 처리 및 복사 기능 렌더링
    renderMaskedToken({ text }) {
      if (!text) return '-';
      // 토큰 마스킹 처리 (앞 10자리만 보여주고 나머지는 * 처리)
      const maskedToken = text.substring(0, 10) + '*'.repeat(Math.min(20, text.length - 10));
      return (
        <a-typography-paragraph copyable={{ text: text }} style={{ marginBottom: 0 }}>
          {maskedToken}
        </a-typography-paragraph>
      );
    },

    // 공통 메서드: 날짜 시간 포맷팅
    renderDateTime({ text }) {
      if (!text) return '-';
      return this.formatUTCDate(new Date(text));
    },

    // 공통 메서드: UTC 날짜를 포맷팅하는 유틸리티 함수
    formatUTCDate(date) {
      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const day = String(date.getUTCDate()).padStart(2, '0');
      const hours = String(date.getUTCHours()).padStart(2, '0');
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      const seconds = String(date.getUTCSeconds()).padStart(2, '0');

      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },

    // 비즈메시지 토큰 목록 조회
    async fetchBizmsgTokenList() {
      try {
        this.bizmsgLoading = true;
        const response = await AlimtalkApi.getTokenList();

        if (response.success) {
          this.bizmsgTokenList = this.processTokenListData(response.data);
        } else {
          this.handleApiError('토큰 목록 조회에 실패했습니다.');
        }
      } catch (error) {
        console.error('비즈메시지 토큰 목록 조회 오류:', error);
        this.handleApiError('토큰 목록 조회 중 오류가 발생했습니다.');
      } finally {
        this.bizmsgLoading = false;
      }
    },

    // 카카오클라우드 토큰 목록 조회
    async fetchKakaocloudTokenList() {
      try {
        this.kakaocloudLoading = true;
        const response = await KakaocloudApi.getTokenList();

        console.log('카카오클라우드 토큰 응답:', response);

        if (response && response.success === true && Array.isArray(response.data)) {
          this.kakaocloudTokenList = this.processTokenListData(response.data);
        } else if (response && response.data && response.data.success === true && Array.isArray(response.data.data)) {
          this.kakaocloudTokenList = this.processTokenListData(response.data.data);
        } else {
          console.error('응답 구조 오류:', response);
          this.handleApiError('카카오클라우드 토큰 목록 조회에 실패했습니다.');
        }
      } catch (error) {
        console.error('카카오클라우드 토큰 목록 조회 오류:', error);
        this.handleApiError('카카오클라우드 토큰 목록 조회 중 오류가 발생했습니다.');
      } finally {
        this.kakaocloudLoading = false;
      }
    },

    // 공통 메서드: 토큰 목록 데이터 처리
    processTokenListData(data) {
      return data.map((item, index) => ({
        ...item,
        index,
        key: item._id || index
      }));
    },

    // 공통 메서드: API 오류 처리
    handleApiError(errorMessage) {
      message.error(errorMessage);
    },

    // 비즈메시지 토큰 갱신
    async refreshBizmsgToken() {
      try {
        this.bizmsgRefreshLoading = true;
        const response = await AlimtalkApi.refreshToken();

        const title = response.success ? '토큰 갱신 성공' : '토큰 갱신 실패';
        const text = response.message ||
          (response.success ? '토큰이 성공적으로 갱신되었습니다.' : '토큰 갱신에 실패했습니다.');

        await oneBtnDialog(title, text);

        if (response.success) {
          await this.fetchBizmsgTokenList();
        }
      } catch (error) {
        console.error('비즈메시지 토큰 갱신 오류:', error);
        await oneBtnDialog('토큰 갱신 실패', '토큰 갱신 중 오류가 발생했습니다.');
      } finally {
        this.bizmsgRefreshLoading = false;
      }
    },

    // 카카오클라우드 토큰 갱신
    async refreshKakaocloudToken() {
      try {
        this.kakaocloudRefreshLoading = true;
        const response = await KakaocloudApi.refreshToken();

        const title = response.success ? '카카오클라우드 토큰 갱신 성공' : '카카오클라우드 토큰 갱신 실패';
        const text = response.message ||
          (response.success ? '카카오클라우드 토큰이 성공적으로 갱신되었습니다.' : '카카오클라우드 토큰 갱신에 실패했습니다.');

        await oneBtnDialog(title, text);

        if (response.success) {
          await this.fetchKakaocloudTokenList();
        }
      } catch (error) {
        console.error('카카오클라우드 토큰 갱신 오류:', error);
        await oneBtnDialog('카카오클라우드 토큰 갱신 실패', '카카오클라우드 토큰 갱신 중 오류가 발생했습니다.');
      } finally {
        this.kakaocloudRefreshLoading = false;
      }
    }
  },
  watch: {
    activeTab: {
      immediate: true,
      handler(newTab) {
        if (newTab === 'bizmsg' && this.bizmsgTokenList.length === 0) {
          this.fetchBizmsgTokenList();
        } else if (newTab === 'kakaocloud' && this.kakaocloudTokenList.length === 0) {
          this.fetchKakaocloudTokenList();
        }
      }
    }
  }
});
</script>

<style scoped>
.container-wrapper {
  background-color: #fff;
  border-radius: 10px;
  padding: 20px;
  margin-top: 20px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.pageTitle {
  margin-bottom: 10px;
}

.pageDesc {
  color: #666;
  font-size: 14px;
}

.tab-header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
}

.noResult {
  text-align: center;
  padding: 20px;
  color: #999;
}

:deep(.ant-table-striped) :deep(.ant-table-body) tr:nth-child(2n) td {
  background-color: #fafafa;
}

:deep(.ant-tabs-card) > .ant-tabs-nav .ant-tabs-tab {
  border-radius: 8px 8px 0 0;
}

:deep(.ant-tabs-card) > .ant-tabs-nav .ant-tabs-tab-active {
  background-color: #fff;
  border-color: #d9d9d9;
}

.coming-soon-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 40px;
}

.coming-soon-content {
  text-align: center;
  max-width: 500px;
}

.coming-soon-content h2 {
  font-size: 24px;
  font-weight: 600;
  color: #595959;
  margin-bottom: 12px;
}

.coming-soon-content p {
  font-size: 16px;
  color: #8c8c8c;
  line-height: 1.6;
  margin: 0;
}
</style>
